import { useEffect, useState, useCallback } from "react";

const NAMES = [
  "Carlos M.", "María G.", "Antonio R.", "Lucía P.", "Javier S.",
  "Carmen L.", "Manuel F.", "Isabel D.", "Francisco J.", "Ana B.",
  "Diego H.", "Sofía T.",
];

const SERVICES = [
  "reparación de persiana",
  "motorización de persiana",
  "instalación de toldo",
  "cambio de cinta",
  "mosquitera enrollable",
  "presupuesto para toldo",
  "reparación de motor",
];

const CITIES = [
  "Málaga", "Marbella", "Fuengirola", "Torremolinos", "Benalmádena",
  "Mijas", "Estepona", "Vélez-Málaga", "Rincón de la Victoria", "Nerja",
  "Antequera", "Ronda", "Benahavís",
];

const TIMES = [
  "hace 3 min", "hace 8 min", "hace 15 min", "hace 22 min", "hace 35 min",
  "hace 47 min", "hace 1 hora", "hace 12 min", "hace 28 min",
];

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function generateMessage(): string {
  return `${randomItem(NAMES)} acaba de pedir ${randomItem(SERVICES)} · ${randomItem(CITIES)} · ${randomItem(TIMES)}`;
}

function getNextDelay(): number {
  // 60-90s in ms
  return 60000 + Math.floor(Math.random() * 30000);
}

export default function SocialProofPopup() {
  const [visible, setVisible] = useState(false);
  const [entering, setEntering] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [message, setMessage] = useState("");
  const [closedByUser, setClosedByUser] = useState(false);

  const showPopup = useCallback(() => {
    if (closedByUser) return;
    setMessage(generateMessage());
    setEntering(true);
    setLeaving(false);
    setVisible(true);
    // Remove enter class after animation
    setTimeout(() => setEntering(false), 320);
    // Auto hide after 7s
    setTimeout(() => {
      if (closedByUser) return;
      setLeaving(true);
      setTimeout(() => {
        setVisible(false);
        setLeaving(false);
      }, 320);
    }, 7000);
  }, [closedByUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem("socialproof_closed") === "1") {
        setClosedByUser(true);
        return;
      }
    } catch {
      // ignore
    }

    const firstTimer = setTimeout(showPopup, 8000);
    let nextTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      nextTimer = setTimeout(() => {
        showPopup();
        scheduleNext();
      }, getNextDelay());
    };

    const afterFirst = setTimeout(scheduleNext, 8000 + 7000 + 500);

    return () => {
      clearTimeout(firstTimer);
      clearTimeout(afterFirst);
      if (nextTimer) clearTimeout(nextTimer);
    };
  }, [showPopup]);

  const handleClose = () => {
    setLeaving(true);
    setTimeout(() => {
      setVisible(false);
      setLeaving(false);
    }, 320);
    setClosedByUser(true);
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("socialproof_closed", "1");
      }
    } catch {
      // ignore
    }
  };

  if (!visible) return null;

  const baseClasses =
    "fixed z-[9999] bg-white rounded-xl p-3.5 md:p-4 shadow-[0_4px_12px_rgba(0,0,0,0.15)] flex items-start gap-3 transition-all duration-300 ease-out";
  const enterClasses = entering
    ? "opacity-0 translate-y-4"
    : "opacity-100 translate-y-0";
  const leaveClasses = leaving
    ? "opacity-0 translate-y-4"
    : "opacity-100 translate-y-0";

  return (
    <div
      className={`${baseClasses} ${enterClasses} ${leaveClasses} w-[90%] max-w-[300px] bottom-2.5 left-2.5 md:bottom-5 md:left-5`}
      role="status"
      aria-live="polite"
    >
      <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs md:text-sm text-gray-800 leading-snug font-medium">
          {message}
        </p>
      </div>
      <button
        type="button"
        onClick={handleClose}
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
        aria-label="Cerrar notificación"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}